<template>
  <!-- Breadcrumb -->
  <div class="mt-4 px-4 container-fluid">
    <div class="breadcrumb text-md px-0 mb-4">
      <p>
        <span class="breadcrumb-item" @click="irInicio">Inicio</span> > Análisis de Ventas
      </p>
    </div>
  </div>

  <div class="px-4 container-fluid">

    <!-- Cabecera + filtros -->
    <div class="row mb-2">
      <div class="col-12">
        <div class="seccion-chip">
          <i class="fas fa-chart-line" style="font-size:10px"></i>
          Ventas
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card ficha-buscador">
          <div class="titulo">
            <h5 class="mx-4 mt-5 mb-1">Análisis de Ventas</h5>
            <p class="mx-4 mb-4 text-sm opacity-7">
              Estadísticas agregadas de ventas: KPIs, distribuciones temporales, países, métodos de pago y tabla por canal.
            </p>
          </div>

          <!-- Selectores: tipster / canal / moneda + Aplicar -->
          <div class="av-selector-row px-4 pt-1 pb-2">
            <div class="av-select-block" v-if="tipsters.length > 1">
              <label class="text-sm mb-1">Tipster</label>
              <div class="select-wrap">
                <select class="form-select" v-model="filters.tipster_id">
                  <option :value="0">-- Todos --</option>
                  <option v-for="t in tipsters" :key="t.id" :value="Number(t.id)">{{ t.nombre }}</option>
                </select>
              </div>
            </div>

            <div class="av-select-block">
              <label class="text-sm mb-1">Canal</label>
              <div class="select-wrap">
                <select class="form-select" v-model="filters.canal_id">
                  <option :value="0">-- Todos --</option>
                  <option v-for="c in canalesFiltrados" :key="c.id" :value="Number(c.id)">{{ c.nombre }}</option>
                </select>
              </div>
            </div>

            <div class="av-select-block">
              <label class="text-sm mb-1">Moneda</label>
              <div class="select-wrap">
                <select class="form-select" v-model="filters.moneda">
                  <option value="">-- Todas --</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>

            <div class="av-selector-actions">
              <button class="btn-accion btn-accion--primary" @click="cargar" :disabled="cargando">
                <i class="fas fa-search me-1"></i>
                {{ cargando ? 'Cargando...' : 'Aplicar' }}
              </button>
              <button class="btn-accion btn-accion--secondary" @click="exportarJSON" :disabled="!datos || cargando">
                <i class="fas fa-download me-1"></i>
                Exportar
              </button>
              <button class="btn-accion btn-accion--secondary" @click="exportarQuantyJson" :disabled="exportandoQuantyJson || !dateRange.desde || !dateRange.hasta" title="Descargar suscriptores con pagos en formato Quanty JSON">
                <i class="fas fa-file-code me-1"></i>
                {{ exportandoQuantyJson ? 'Generando...' : 'Quanty JSON' }}
              </button>
            </div>
          </div>

          <!-- Período -->
          <div class="filtro-periodo px-4 pb-4">
            <div class="periodo-btns">
              <button
                v-for="p in periodos"
                :key="p.value"
                :class="['btn-periodo', { active: periodo === p.value }]"
                :disabled="cargando"
                @click="cambiarPeriodo(p.value)"
              >
                <i v-if="p.value === 'custom'" class="fas fa-calendar-alt me-1"></i>
                {{ p.label }}
              </button>
            </div>
            <div v-if="periodo === 'custom'" class="custom-dates mt-2">
              <input type="date" v-model="filters.desde" class="form-control date-input" :max="filters.hasta || undefined" />
              <span class="sep-dash">—</span>
              <input type="date" v-model="filters.hasta" class="form-control date-input" :min="filters.desde || undefined" />
              <button
                class="btn-accion btn-accion--primary"
                :disabled="!filters.desde || !filters.hasta || cargando"
                @click="cargar"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="error" class="row mt-3">
      <div class="col-12">
        <div class="av-error">{{ error }}</div>
      </div>
    </div>

    <!-- Cargando -->
    <div v-if="cargando && !datos" class="row mt-4">
      <div class="col-12 text-center py-5">
        <i class="fas fa-circle-notch fa-spin" style="font-size:2rem;color:var(--q-gold,#d4af37)"></i>
        <p class="mt-3 text-sm opacity-7">Cargando datos…</p>
      </div>
    </div>

    <!-- Placeholder inicial -->
    <div v-if="!datos && !cargando && !error" class="row mt-4">
      <div class="col-12 text-center py-5 av-placeholder">
        <i class="fas fa-chart-bar av-placeholder-icon"></i>
        <p class="mt-3 av-placeholder-text">Selecciona un período y haz clic en <strong>Aplicar</strong> para ver los datos.</p>
      </div>
    </div>

    <!-- KPIs: Ventas Totales -->
    <div v-if="datos" class="av-kpi-section-header mt-4">
      <span class="av-kpi-section-label av-kpi-section-label--total">
        <i class="fas fa-chart-bar me-1"></i> Ventas totales
      </span>
      <span class="av-kpi-section-sub">con y sin campaña</span>
    </div>
    <div class="row mt-2 g-2" v-if="datos">
      <div class="col-6 col-sm-4 col-lg-2 mb-3">
        <div class="av-kpi-card">
          <div class="av-kpi-icon"><i class="fas fa-shopping-cart"></i></div>
          <div class="av-kpi-valor">{{ datos.kpis.total_pedidos.toLocaleString('es') }}</div>
          <div class="av-kpi-label">Pedidos</div>
          <div class="av-kpi-sub-row" v-if="kpisMoneda.length > 1">
            <span class="av-kpi-sub-badge eur">{{ monedaEur.pedidos }} €</span>
            <span class="av-kpi-sub-badge usd">{{ monedaUsd.pedidos }} $</span>
          </div>
        </div>
      </div>
      <div class="col-6 col-sm-4 col-lg-2 mb-3">
        <div class="av-kpi-card av-kpi-card--eur">
          <div class="av-kpi-icon av-kpi-icon--eur"><i class="fas fa-euro-sign"></i></div>
          <div class="av-kpi-valor av-kpi-valor--eur">{{ formatMoneyEur(monedaEur.ingresos) }}</div>
          <div class="av-kpi-label">Ingresos EUR</div>
          <div class="av-kpi-sub">{{ monedaEur.pedidos }} pedidos</div>
        </div>
      </div>
      <div class="col-6 col-sm-4 col-lg-2 mb-3">
        <div class="av-kpi-card av-kpi-card--usd">
          <div class="av-kpi-icon av-kpi-icon--usd"><i class="fas fa-dollar-sign"></i></div>
          <div class="av-kpi-valor av-kpi-valor--usd">{{ formatMoneyUsd(monedaUsd.ingresos) }}</div>
          <div class="av-kpi-label">Ingresos USD</div>
          <div class="av-kpi-sub">{{ monedaUsd.pedidos }} pedidos</div>
        </div>
      </div>
      <div class="col-6 col-sm-4 col-lg-2 mb-3">
        <div class="av-kpi-card">
          <div class="av-kpi-icon"><i class="fas fa-tag"></i></div>
          <div class="av-kpi-valor">{{ formatMoneyEur(ticketMedioEur) }}</div>
          <div class="av-kpi-label">Ticket medio</div>
          <div class="av-kpi-sub av-kpi-sub--usd" v-if="monedaUsd.pedidos > 0">{{ formatMoneyUsd(ticketMedioUsd) }}</div>
        </div>
      </div>
      <div class="col-6 col-sm-4 col-lg-2 mb-3">
        <div class="av-kpi-card">
          <div class="av-kpi-icon"><i class="fas fa-users"></i></div>
          <div class="av-kpi-valor">{{ datos.kpis.compradores_unicos.toLocaleString('es') }}</div>
          <div class="av-kpi-label">Compradores únicos</div>
        </div>
      </div>
      <div class="col-6 col-sm-4 col-lg-2 mb-3">
        <div class="av-kpi-card">
          <div class="av-kpi-icon"><i class="fas fa-calendar-check"></i></div>
          <div class="av-kpi-valor">{{ datos.kpis.dias_con_ventas.toLocaleString('es') }}</div>
          <div class="av-kpi-label">Días con ventas</div>
        </div>
      </div>
    </div>

    <!-- KPI: Renovaciones (card única) -->
    <div class="row mt-3 g-2" v-if="datos && totalRenovaciones > 0">
      <div class="col-12 col-md-6 col-lg-4 mb-3">
        <div class="av-kpi-card av-kpi-card--renov av-kpi-card--renov-full">
          <div class="av-kpi-renov-header">
            <i class="fas fa-sync-alt av-kpi-icon--renov me-2"></i>
            <span class="av-kpi-section-label--renov fw-semibold">Renovaciones del periodo</span>
          </div>
          <div class="av-kpi-renov-body">
            <div class="av-kpi-renov-row" v-if="datos.kpis_renovaciones.total_eur > 0">
              <span class="av-kpi-sub-badge eur">{{ datos.kpis_renovaciones.total_eur }}</span>
              <span class="av-kpi-valor--eur fw-semibold">{{ formatMoneyEur(datos.kpis_renovaciones.ingresos_eur) }}</span>
            </div>
            <div class="av-kpi-renov-row" v-if="datos.kpis_renovaciones.total_usd > 0">
              <span class="av-kpi-sub-badge usd">{{ datos.kpis_renovaciones.total_usd }}</span>
              <span class="av-kpi-valor--usd fw-semibold">{{ formatMoneyUsd(datos.kpis_renovaciones.ingresos_usd) }}</span>
            </div>
          </div>
          <div class="av-kpi-label mt-1">{{ totalRenovaciones }} cobros recurrentes</div>
        </div>
      </div>
    </div>

    <!-- KPIs: Solo con campaña -->
    <div v-if="datos && datos.kpis_campana && datos.kpis_campana.total_pedidos > 0" class="av-kpi-section-header mt-3">
      <span class="av-kpi-section-label av-kpi-section-label--campana av-kpi-section-label--big">
        <i class="fas fa-bullhorn me-1"></i> Con campaña
      </span>
      <span class="av-kpi-section-sub">{{ pctAtribuido }}% del total atribuido</span>
    </div>
    <div class="row mt-2 g-2" v-if="datos && datos.kpis_campana && datos.kpis_campana.total_pedidos > 0">
      <div class="col-6 col-sm-4 col-lg-2 mb-3">
        <div class="av-kpi-card av-kpi-card--campana">
          <div class="av-kpi-icon av-kpi-icon--campana"><i class="fas fa-shopping-cart"></i></div>
          <div class="av-kpi-valor">{{ datos.kpis_campana.total_pedidos.toLocaleString('es') }}</div>
          <div class="av-kpi-label">Pedidos</div>
          <div class="av-kpi-sub-row" v-if="monedaEurCampana.pedidos > 0 && monedaUsdCampana.pedidos > 0">
            <span class="av-kpi-sub-badge eur">{{ monedaEurCampana.pedidos }} €</span>
            <span class="av-kpi-sub-badge usd">{{ monedaUsdCampana.pedidos }} $</span>
          </div>
        </div>
      </div>
      <div class="col-6 col-sm-4 col-lg-2 mb-3">
        <div class="av-kpi-card av-kpi-card--eur">
          <div class="av-kpi-icon av-kpi-icon--eur"><i class="fas fa-euro-sign"></i></div>
          <div class="av-kpi-valor av-kpi-valor--eur">{{ formatMoneyEur(monedaEurCampana.ingresos) }}</div>
          <div class="av-kpi-label">Ingresos EUR</div>
          <div class="av-kpi-sub">{{ monedaEurCampana.pedidos }} pedidos</div>
        </div>
      </div>
      <div class="col-6 col-sm-4 col-lg-2 mb-3">
        <div class="av-kpi-card av-kpi-card--usd">
          <div class="av-kpi-icon av-kpi-icon--usd"><i class="fas fa-dollar-sign"></i></div>
          <div class="av-kpi-valor av-kpi-valor--usd">{{ formatMoneyUsd(monedaUsdCampana.ingresos) }}</div>
          <div class="av-kpi-label">Ingresos USD</div>
          <div class="av-kpi-sub">{{ monedaUsdCampana.pedidos }} pedidos</div>
        </div>
      </div>
      <div class="col-6 col-sm-4 col-lg-2 mb-3">
        <div class="av-kpi-card">
          <div class="av-kpi-icon"><i class="fas fa-tag"></i></div>
          <div class="av-kpi-valor">{{ formatMoneyEur(ticketMedioEurCampana) }}</div>
          <div class="av-kpi-label">Ticket medio</div>
          <div class="av-kpi-sub av-kpi-sub--usd" v-if="monedaUsdCampana.pedidos > 0">{{ formatMoneyUsd(ticketMedioUsdCampana) }}</div>
        </div>
      </div>
      <div class="col-6 col-sm-4 col-lg-2 mb-3">
        <div class="av-kpi-card av-kpi-card--campana">
          <div class="av-kpi-icon av-kpi-icon--campana"><i class="fas fa-users"></i></div>
          <div class="av-kpi-valor">{{ datos.kpis_campana.compradores_unicos.toLocaleString('es') }}</div>
          <div class="av-kpi-label">Compradores únicos</div>
        </div>
      </div>
    </div>

    <!-- Heatmap: actividad horaria (día × hora) -->
    <div class="row mt-2" v-if="datos && datos.heatmap && datos.heatmap.length">
      <div class="col-12 mb-4">
        <PanelTipsterHeatmap :key="'hm-' + chartKey" :heatmap="datos.heatmap" />
      </div>
    </div>

    <!-- Lista: países con banderas -->
    <div class="row mt-2" v-if="datos && datos.por_pais && datos.por_pais.length">
      <div class="col-12 mb-4">
        <div class="q-chart-card">
          <div class="q-chart-head">
            <div>
              <p class="q-chart-title">Pedidos por país</p>
              <p class="q-chart-subtitle">Top 15 países de compra</p>
            </div>
          </div>
          <div class="av-paises-lista px-3 pt-2 pb-3">
            <div v-for="r in datos.por_pais" :key="r.pais" class="av-pais-row">
              <span :class="['fi', flagClass(r.pais)]" class="av-pais-flag"></span>
              <span class="av-pais-nombre">{{ paisNombre(r.pais) }}</span>
              <div class="av-pais-bar-wrap">
                <div class="av-pais-bar" :style="{ width: (Number(r.pedidos) / maxPaisPedidos * 100) + '%' }"></div>
              </div>
              <span class="av-pais-num">{{ Number(r.pedidos).toLocaleString('es') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Chart: métodos de pago -->
    <div class="row mt-2" v-if="datos">
      <div class="col-12 mb-4">
        <AvMetodosPago
          :key="'mp-' + chartKey"
          :datos="datos ? datos.por_metodo_pago : []"
        />
      </div>
    </div>

    <!-- Evolución diaria -->
    <div class="row mt-2" v-if="datos && evolucionLabels.length">
      <div class="col-12 mb-4">
        <div class="q-chart-card">
          <div class="q-chart-head">
            <div>
              <p class="q-chart-title">Evolución de pedidos</p>
              <p class="q-chart-subtitle">Pedidos confirmados por día</p>
            </div>
          </div>
          <QuantyEvolucionChart
            :key="'evol-' + chartKey"
            id="avEvolucion"
            :labels="evolucionLabels"
            :ventasNuevas="evolucionPedidos"
            :ventasRenov="evolucionRenovaciones"
            :importeEur="evolucionImporte"
            :importeUsd="[]"
          />
        </div>
      </div>
    </div>

    <!-- Tabla renovaciones del periodo -->
    <div v-if="datos && datos.renovaciones_lista && datos.renovaciones_lista.length" class="row mt-4">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0 d-flex align-items-center justify-content-between">
            <h6 class="av-tabla-titulo mb-0"><i class="fas fa-sync-alt me-2 av-kpi-icon--renov"></i>Renovaciones del periodo</h6>
            <span class="text-xs opacity-5">{{ datos.renovaciones_lista.length }} cobros recurrentes</span>
          </div>
          <div class="table-responsive">
            <table class="table align-items-center mb-0 av-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Suscriptor</th>
                  <th>Producto</th>
                  <th>Canal</th>
                  <th>Campaña</th>
                  <th class="text-end">Importe</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in datos.renovaciones_lista" :key="i">
                  <td class="text-sm">{{ r.fecha }}</td>
                  <td class="text-sm opacity-7">{{ r.email || '—' }}</td>
                  <td class="text-sm">{{ r.producto_nombre }}</td>
                  <td class="text-sm opacity-7">{{ r.canal_nombre }}</td>
                  <td class="text-sm">
                    <span v-if="r.campana_nombre" class="badge badge-sm av-badge-campana">{{ r.campana_nombre }}</span>
                    <span v-else class="opacity-4 text-xs">directo</span>
                  </td>
                  <td class="text-end av-num" :class="r.moneda === 'USD' ? 'av-num--usd' : 'av-num--eur'">
                    {{ r.moneda === 'USD' ? formatMoneyUsd(r.importe) : formatMoneyEur(r.importe) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- KPI renovaciones con campaña -->
    <div v-if="datos && totalRenovacionesCampana > 0" class="row mt-3 g-2">
      <div class="col-12 col-md-6 col-lg-4 mb-3">
        <div class="av-kpi-card av-kpi-card--renov av-kpi-card--renov-full">
          <div class="av-kpi-renov-header">
            <i class="fas fa-bullhorn av-kpi-icon--renov me-2"></i>
            <span class="av-kpi-section-label--renov fw-semibold">Renovaciones con campaña</span>
          </div>
          <div class="av-kpi-renov-body">
            <div class="av-kpi-renov-row" v-if="datos.kpis_renovaciones_campana.total_eur > 0">
              <span class="av-kpi-sub-badge eur">{{ datos.kpis_renovaciones_campana.total_eur }}</span>
              <span class="av-kpi-valor--eur fw-semibold">{{ formatMoneyEur(datos.kpis_renovaciones_campana.ingresos_eur) }}</span>
            </div>
            <div class="av-kpi-renov-row" v-if="datos.kpis_renovaciones_campana.total_usd > 0">
              <span class="av-kpi-sub-badge usd">{{ datos.kpis_renovaciones_campana.total_usd }}</span>
              <span class="av-kpi-valor--usd fw-semibold">{{ formatMoneyUsd(datos.kpis_renovaciones_campana.ingresos_usd) }}</span>
            </div>
          </div>
          <div class="av-kpi-label mt-1">{{ totalRenovacionesCampana }} renovaciones atribuidas</div>
        </div>
      </div>
    </div>

    <!-- Tabla renovaciones por campaña -->
    <div v-if="datos && datos.renovaciones_por_campana && datos.renovaciones_por_campana.length" class="row mt-3">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0 d-flex align-items-center justify-content-between">
            <h6 class="av-tabla-titulo mb-0"><i class="fas fa-bullhorn me-2 av-kpi-icon--renov"></i>Renovaciones por campaña</h6>
            <span class="text-xs opacity-5">ingresos recurrentes atribuidos</span>
          </div>
          <div class="table-responsive">
            <table class="table align-items-center mb-0 av-table">
              <thead>
                <tr>
                  <th>Campaña</th>
                  <th>Canal</th>
                  <th class="text-end">Renovaciones</th>
                  <th class="text-end tc-th--eur">EUR</th>
                  <th class="text-end tc-th--usd">USD</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in renovPorCampanaSorted" :key="i">
                  <td class="av-td-campana">{{ r.campana_nombre }}<small class="d-block opacity-5">{{ r.tipster_nombre }}</small></td>
                  <td class="text-sm opacity-7">{{ r.canal_nombre }}</td>
                  <td class="text-end av-num">{{ Number(r.renovaciones).toLocaleString('es') }}</td>
                  <td class="text-end av-num av-num--eur">
                    <template v-if="r.ventas_eur > 0">
                      <span class="av-count-badge eur">{{ r.ventas_eur }}</span>
                      {{ formatMoneyEur(r.importe_eur) }}
                    </template>
                    <span v-else class="opacity-4">—</span>
                  </td>
                  <td class="text-end av-num av-num--usd">
                    <template v-if="r.ventas_usd > 0">
                      <span class="av-count-badge usd">{{ r.ventas_usd }}</span>
                      {{ formatMoneyUsd(r.importe_usd) }}
                    </template>
                    <span v-else class="opacity-4">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla por campaña -->
    <div v-if="datos && datos.por_campana && datos.por_campana.length">
      <!-- Cabecera con título + selector columnas -->
      <div class="row mt-2">
        <div class="col-12 mb-2 d-flex align-items-center justify-content-between">
          <h6 class="av-tabla-titulo mb-0">Ventas por campaña</h6>
          <div class="av-col-wrap" v-click-outside="() => campanaColsOpen = false">
            <button class="av-col-btn" @click="campanaColsOpen = !campanaColsOpen" :class="{ activo: campanaColsOpen }" title="Columnas visibles">
              <i class="fas fa-columns"></i>
            </button>
            <div v-if="campanaColsOpen" class="av-col-panel">
              <div class="av-col-panel-hdr">
                <span>Columnas</span>
                <button class="av-col-reset" @click="resetCampanaCols">Restablecer</button>
              </div>
              <div class="av-col-panel-grid">
                <label v-for="col in campanaColsConfig" :key="col.key" class="av-col-item">
                  <input type="checkbox" v-model="campanaColsVisible[col.key]" @change="saveCampanaCols" />
                  <span>{{ col.label }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Una tabla por tipster (si hay más de uno) -->
      <div v-for="grupo in campanaPorTipster" :key="grupo.tipster" class="row mb-4">
        <div class="col-12">
          <div v-if="campanaPorTipster.length > 1" class="av-tipster-sep">
            <i class="fas fa-user-tie me-1"></i>{{ grupo.tipster }}
          </div>
          <div class="card">
            <div class="card-header pb-0 d-flex align-items-center justify-content-between">
              <span class="text-sm opacity-7">{{ grupo.rows.length }} campañas</span>
              <span class="text-xs opacity-5">Solo compras confirmadas</span>
            </div>
            <div class="table-responsive">
              <table class="table align-items-center mb-0 av-table">
                <thead>
                  <tr>
                    <th class="av-th-sort" @click="ordenarCampana('campana')">
                      Campaña <i :class="campanaSortIcon('campana')"></i>
                    </th>
                    <th class="av-th-sort" :class="{ 'd-none': !campanaColsVisible.canal }" @click="ordenarCampana('canal')">
                      Canal <i :class="campanaSortIcon('canal')"></i>
                    </th>
                    <th class="av-th-sort text-end" :class="{ 'd-none': !campanaColsVisible.pedidos }" @click="ordenarCampana('pedidos')">
                      Pedidos <i :class="campanaSortIcon('pedidos')"></i>
                    </th>
                    <th class="av-th-sort text-end tc-th--eur" :class="{ 'd-none': !campanaColsVisible.eur }" @click="ordenarCampana('eur')">
                      EUR <i :class="campanaSortIcon('eur')"></i>
                    </th>
                    <th class="av-th-sort text-end tc-th--usd" :class="{ 'd-none': !campanaColsVisible.usd }" @click="ordenarCampana('usd')">
                      USD <i :class="campanaSortIcon('usd')"></i>
                    </th>
                    <th class="av-th-sort text-end" :class="{ 'd-none': !campanaColsVisible.compradores }" @click="ordenarCampana('compradores')">
                      Compradores <i :class="campanaSortIcon('compradores')"></i>
                    </th>
                    <th class="av-th-sort text-end" :class="{ 'd-none': !campanaColsVisible.ticket }" @click="ordenarCampana('ticket')">
                      Ticket <i :class="campanaSortIcon('ticket')"></i>
                    </th>
                    <th style="width:36px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in campanaRowsPaged(campanaRowsSorted(grupo.rows), grupo.tipster)" :key="row.campana_id">
                    <td class="av-td-campana">
                      {{ row.campana_nombre }}
                      <small v-if="row.meta_campaign_name" class="d-block av-meta-name">{{ row.meta_campaign_name }}</small>
                    </td>
                    <td :class="{ 'd-none': !campanaColsVisible.canal }" class="text-sm opacity-7">{{ row.canal_nombre }}</td>
                    <td :class="{ 'd-none': !campanaColsVisible.pedidos }" class="text-end av-num">{{ Number(row.pedidos).toLocaleString('es') }}</td>
                    <td :class="{ 'd-none': !campanaColsVisible.eur }" class="text-end av-num av-num--eur">
                      <template v-if="row.ventas_eur > 0">
                        <span class="av-count-badge eur">{{ row.ventas_eur }}</span>
                        {{ formatMoneyEur(row.importe_eur) }}
                      </template>
                      <span v-else class="opacity-4">—</span>
                    </td>
                    <td :class="{ 'd-none': !campanaColsVisible.usd }" class="text-end av-num av-num--usd">
                      <template v-if="row.ventas_usd > 0">
                        <span class="av-count-badge usd">{{ row.ventas_usd }}</span>
                        {{ formatMoneyUsd(row.importe_usd) }}
                      </template>
                      <span v-else class="opacity-4">—</span>
                    </td>
                    <td :class="{ 'd-none': !campanaColsVisible.compradores }" class="text-end">{{ Number(row.compradores_unicos).toLocaleString('es') }}</td>
                    <td :class="{ 'd-none': !campanaColsVisible.ticket }" class="text-end av-num opacity-8">{{ formatMoneyEur(row.ticket_medio) }}</td>
                    <td class="text-center" style="padding:6px 8px">
                      <button class="av-ojo-btn" @click="abrirDetalle(row)" title="Ver productos">
                        <i class="fas fa-eye"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Paginación -->
            <div v-if="campanaTotalPages(grupo.rows) > 1" class="av-pagination">
              <button class="av-page-btn" :disabled="campanaGetPage(grupo.tipster) <= 1"
                @click="campanaSetPage(grupo.tipster, campanaGetPage(grupo.tipster) - 1)">
                <i class="fas fa-chevron-left"></i>
              </button>
              <span class="av-page-info">
                Pág. {{ campanaGetPage(grupo.tipster) }} de {{ campanaTotalPages(grupo.rows) }}
                <span class="opacity-5 ms-1">({{ grupo.rows.length }} total)</span>
              </span>
              <button class="av-page-btn" :disabled="campanaGetPage(grupo.tipster) >= campanaTotalPages(grupo.rows)"
                @click="campanaSetPage(grupo.tipster, campanaGetPage(grupo.tipster) + 1)">
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════
         TABLA: Ventas totales por producto (con y sin campaña)
    ════════════════════════════════════════════════════════════════ -->
    <div v-if="datos && datos.por_producto && datos.por_producto.length" class="mt-4">
      <div class="row">
        <div class="col-12 mb-2 d-flex align-items-center">
          <h6 class="av-tabla-titulo mb-0">Ventas totales por producto</h6>
          <span class="av-tabla-sub ms-2">con y sin campaña</span>
        </div>
      </div>

      <div v-for="grupo in productoPorTipster" :key="grupo.tipster" class="row mb-4">
        <div class="col-12">
          <div v-if="productoPorTipster.length > 1" class="av-tipster-sep">
            <i class="fas fa-user-tie me-1"></i>{{ grupo.tipster }}
          </div>
          <div class="card">
            <div class="card-header pb-0 d-flex align-items-center justify-content-between">
              <span class="text-sm opacity-7">{{ grupo.rows.length }} productos</span>
              <span class="text-xs opacity-5">Solo compras confirmadas</span>
            </div>
            <div class="table-responsive">
              <table class="table align-items-center mb-0 av-table">
                <thead>
                  <tr>
                    <th class="av-th-sort" @click="ordenarProducto('producto')">
                      Producto <i :class="productoSortIcon('producto')"></i>
                    </th>
                    <th class="av-th-sort" @click="ordenarProducto('canal')">
                      Canal <i :class="productoSortIcon('canal')"></i>
                    </th>
                    <th class="av-th-sort text-end" @click="ordenarProducto('pedidos_total')">
                      Total <i :class="productoSortIcon('pedidos_total')"></i>
                    </th>
                    <th class="av-th-sort text-end" @click="ordenarProducto('campana')">
                      Campaña <i :class="productoSortIcon('campana')"></i>
                    </th>
                    <th class="av-th-sort text-end" @click="ordenarProducto('directo')">
                      Directo <i :class="productoSortIcon('directo')"></i>
                    </th>
                    <th class="av-th-sort text-end tc-th--eur" @click="ordenarProducto('eur')">
                      EUR <i :class="productoSortIcon('eur')"></i>
                    </th>
                    <th class="av-th-sort text-end tc-th--usd" @click="ordenarProducto('usd')">
                      USD <i :class="productoSortIcon('usd')"></i>
                    </th>
                    <th style="width:36px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in productoRowsPaged(productoRowsSorted(grupo.rows), grupo.tipster)" :key="row.producto_id + '-' + row.canal_nombre">
                    <td style="font-weight:600">{{ row.producto_nombre }}</td>
                    <td class="text-sm opacity-7">{{ row.canal_nombre }}</td>
                    <td class="text-end av-num">{{ Number(row.pedidos_total).toLocaleString('es') }}</td>
                    <td class="text-end av-num">
                      <template v-if="row.pedidos_campana > 0">
                        <span class="av-campana-badge">
                          <i class="fas fa-bullhorn"></i> {{ Number(row.pedidos_campana).toLocaleString('es') }}
                        </span>
                        <span class="av-pct-campana">{{ pctCampana(row) }}%</span>
                      </template>
                      <span v-else class="opacity-4">—</span>
                    </td>
                    <td class="text-end av-num opacity-7">
                      {{ row.pedidos_directo > 0 ? Number(row.pedidos_directo).toLocaleString('es') : '—' }}
                    </td>
                    <td class="text-end av-num av-num--eur">
                      <template v-if="row.ventas_eur > 0">
                        <span class="av-count-badge eur">{{ row.ventas_eur }}</span>
                        {{ formatMoneyEur(row.importe_eur) }}
                      </template>
                      <span v-else class="opacity-4">—</span>
                    </td>
                    <td class="text-end av-num av-num--usd">
                      <template v-if="row.ventas_usd > 0">
                        <span class="av-count-badge usd">{{ row.ventas_usd }}</span>
                        {{ formatMoneyUsd(row.importe_usd) }}
                      </template>
                      <span v-else class="opacity-4">—</span>
                    </td>
                    <td class="text-center" style="padding:6px 8px">
                      <button class="av-ojo-btn" @click="abrirDetalleProducto(row)" title="Ver desglose campaña / directo">
                        <i class="fas fa-eye"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="productoTotalPages(grupo.rows) > 1" class="av-pagination">
              <button class="av-page-btn" :disabled="productoGetPage(grupo.tipster) <= 1"
                @click="productoSetPage(grupo.tipster, productoGetPage(grupo.tipster) - 1)">
                <i class="fas fa-chevron-left"></i>
              </button>
              <span class="av-page-info">
                Pág. {{ productoGetPage(grupo.tipster) }} de {{ productoTotalPages(grupo.rows) }}
                <span class="opacity-5 ms-1">({{ grupo.rows.length }} total)</span>
              </span>
              <button class="av-page-btn" :disabled="productoGetPage(grupo.tipster) >= productoTotalPages(grupo.rows)"
                @click="productoSetPage(grupo.tipster, productoGetPage(grupo.tipster) + 1)">
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal detalle producto -->
    <transition name="av-modal-fade">
      <div v-if="productoModal.open" class="av-modal-backdrop" @click.self="cerrarDetalleProducto">
        <div class="av-modal-box av-modal-box--lg">
          <div class="av-modal-hdr">
            <h6 class="av-modal-titulo mb-0">{{ productoModal.producto_nombre }}</h6>
            <button class="av-modal-close" @click="cerrarDetalleProducto"><i class="fas fa-times"></i></button>
          </div>

          <div v-if="productoModal.loading" class="av-modal-body text-center py-4">
            <i class="fas fa-circle-notch fa-spin" style="font-size:1.5rem;color:var(--q-gold,#d4af37)"></i>
          </div>

          <div v-else class="av-modal-body">
            <!-- Ventas por campaña -->
            <p class="av-modal-seccion-titulo">
              <i class="fas fa-bullhorn me-1"></i>Con campaña
              <span class="av-modal-seccion-badge">{{ productoModal.porCampana.length }}</span>
            </p>
            <div v-if="productoModal.porCampana.length" class="table-responsive mb-3">
              <table class="table align-items-center mb-0 av-table">
                <thead>
                  <tr>
                    <th>Campaña</th>
                    <th class="text-end">Ped.</th>
                    <th class="text-end tc-th--eur">EUR</th>
                    <th class="text-end tc-th--usd">USD</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in productoModal.porCampana" :key="c.campana_id">
                    <td style="font-weight:500">{{ c.campana_nombre }}</td>
                    <td class="text-end av-num">{{ Number(c.pedidos).toLocaleString('es') }}</td>
                    <td class="text-end av-num av-num--eur">
                      <template v-if="c.ventas_eur > 0">
                        <span class="av-count-badge eur">{{ c.ventas_eur }}</span>{{ formatMoneyEur(c.importe_eur) }}
                      </template>
                      <span v-else class="opacity-4">—</span>
                    </td>
                    <td class="text-end av-num av-num--usd">
                      <template v-if="c.ventas_usd > 0">
                        <span class="av-count-badge usd">{{ c.ventas_usd }}</span>{{ formatMoneyUsd(c.importe_usd) }}
                      </template>
                      <span v-else class="opacity-4">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-else class="text-sm opacity-5 mb-3">Sin ventas atribuidas a campañas.</p>

            <!-- Ventas directas -->
            <p class="av-modal-seccion-titulo">
              <i class="fas fa-user me-1"></i>Sin campaña (directo)
            </p>
            <div v-if="productoModal.sinCampana && productoModal.sinCampana.pedidos > 0" class="av-directo-row">
              <span class="av-num">{{ Number(productoModal.sinCampana.pedidos).toLocaleString('es') }} pedidos</span>
              <span v-if="productoModal.sinCampana.ventas_eur > 0" class="av-num av-num--eur ms-3">
                <span class="av-count-badge eur">{{ productoModal.sinCampana.ventas_eur }}</span>{{ formatMoneyEur(productoModal.sinCampana.importe_eur) }}
              </span>
              <span v-if="productoModal.sinCampana.ventas_usd > 0" class="av-num av-num--usd ms-3">
                <span class="av-count-badge usd">{{ productoModal.sinCampana.ventas_usd }}</span>{{ formatMoneyUsd(productoModal.sinCampana.importe_usd) }}
              </span>
            </div>
            <p v-else class="text-sm opacity-5">Sin ventas directas en este período.</p>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal detalle campaña -->
    <transition name="av-modal-fade">
      <div v-if="detalleModal.open" class="av-modal-backdrop" @click.self="cerrarDetalle">
        <div class="av-modal-box">
          <div class="av-modal-hdr">
            <div>
              <h6 class="av-modal-titulo mb-0">{{ detalleModal.campana_nombre }}</h6>
              <p v-if="detalleModal.meta_nombre" class="av-meta-name mb-0 mt-1">{{ detalleModal.meta_nombre }}</p>
            </div>
            <button class="av-modal-close" @click="cerrarDetalle"><i class="fas fa-times"></i></button>
          </div>

          <div v-if="detalleModal.loading" class="av-modal-body text-center py-4">
            <i class="fas fa-circle-notch fa-spin" style="font-size:1.5rem;color:var(--q-gold,#d4af37)"></i>
          </div>

          <div v-else-if="detalleModal.rows.length" class="av-modal-body">
            <div class="table-responsive">
              <table class="table align-items-center mb-0 av-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Canal</th>
                    <th class="text-end">Ped.</th>
                    <th class="text-end tc-th--eur">EUR</th>
                    <th class="text-end tc-th--usd">USD</th>
                    <th class="text-end" style="font-size:.68rem;opacity:.6">Primera</th>
                    <th class="text-end" style="font-size:.68rem;opacity:.6">Última</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="p in detalleModal.rows" :key="p.producto_id + '-' + p.canal_nombre">
                    <td style="font-weight:600">{{ p.producto_nombre }}</td>
                    <td class="text-sm opacity-7">{{ p.canal_nombre }}</td>
                    <td class="text-end av-num">{{ Number(p.pedidos).toLocaleString('es') }}</td>
                    <td class="text-end av-num av-num--eur">
                      <template v-if="p.ventas_eur > 0">
                        <span class="av-count-badge eur">{{ p.ventas_eur }}</span>{{ formatMoneyEur(p.importe_eur) }}
                      </template>
                      <span v-else class="opacity-4">—</span>
                    </td>
                    <td class="text-end av-num av-num--usd">
                      <template v-if="p.ventas_usd > 0">
                        <span class="av-count-badge usd">{{ p.ventas_usd }}</span>{{ formatMoneyUsd(p.importe_usd) }}
                      </template>
                      <span v-else class="opacity-4">—</span>
                    </td>
                    <td class="text-end" style="font-size:.75rem;opacity:.6;white-space:nowrap">{{ fmtFecha(p.primera_compra) }}</td>
                    <td class="text-end" style="font-size:.75rem;opacity:.6;white-space:nowrap">{{ fmtFecha(p.ultima_compra) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-else class="av-modal-body text-center py-4 opacity-5">
            Sin productos para esta campaña.
          </div>
        </div>
      </div>
    </transition>

    <!-- Estado vacío -->
    <div class="row mt-4" v-if="datos && !datos.kpis.total_pedidos && !cargando && !error">
      <div class="col-12">
        <div class="av-empty">
          <i class="fas fa-chart-bar av-empty-icon"></i>
          <p>No hay pedidos para los filtros seleccionados.</p>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
const NOMBRES_PAISES = {
  AD:'Andorra', AE:'Emiratos Árabes', AF:'Afganistán', AL:'Albania', AM:'Armenia',
  AO:'Angola', AR:'Argentina', AT:'Austria', AU:'Australia', AZ:'Azerbaiyán',
  BA:'Bosnia y Herzegovina', BB:'Barbados', BD:'Bangladés', BE:'Bélgica',
  BF:'Burkina Faso', BG:'Bulgaria', BH:'Baréin', BI:'Burundi', BJ:'Benín',
  BN:'Brunéi', BO:'Bolivia', BR:'Brasil', BS:'Bahamas', BT:'Bután',
  BW:'Botsuana', BY:'Bielorrusia', BZ:'Belice', CA:'Canadá', CD:'R. D. del Congo',
  CF:'Rep. Centroafricana', CG:'Congo', CH:'Suiza', CI:'Costa de Marfil',
  CL:'Chile', CM:'Camerún', CN:'China', CO:'Colombia', CR:'Costa Rica',
  CU:'Cuba', CV:'Cabo Verde', CY:'Chipre', CZ:'Chequia', DE:'Alemania',
  DJ:'Yibuti', DK:'Dinamarca', DM:'Dominica', DO:'Rep. Dominicana',
  DZ:'Argelia', EC:'Ecuador', EE:'Estonia', EG:'Egipto', ER:'Eritrea',
  ES:'España', ET:'Etiopía', FI:'Finlandia', FJ:'Fiyi', FR:'Francia',
  GA:'Gabón', GB:'Reino Unido', GD:'Granada', GE:'Georgia', GH:'Ghana',
  GM:'Gambia', GN:'Guinea', GQ:'Guinea Ecuatorial', GR:'Grecia',
  GT:'Guatemala', GW:'Guinea-Bisáu', GY:'Guyana', HN:'Honduras',
  HR:'Croacia', HT:'Haití', HU:'Hungría', ID:'Indonesia', IE:'Irlanda',
  IL:'Israel', IN:'India', IQ:'Irak', IR:'Irán', IS:'Islandia', IT:'Italia',
  JM:'Jamaica', JO:'Jordania', JP:'Japón', KE:'Kenia', KG:'Kirguistán',
  KH:'Camboya', KM:'Comoras', KP:'Corea del Norte', KR:'Corea del Sur',
  KW:'Kuwait', KZ:'Kazajistán', LA:'Laos', LB:'Líbano', LC:'Santa Lucía',
  LI:'Liechtenstein', LK:'Sri Lanka', LR:'Liberia', LS:'Lesoto',
  LT:'Lituania', LU:'Luxemburgo', LV:'Letonia', LY:'Libia', MA:'Marruecos',
  MC:'Mónaco', MD:'Moldavia', ME:'Montenegro', MG:'Madagascar',
  MK:'Macedonia del Norte', ML:'Malí', MM:'Myanmar', MN:'Mongolia',
  MR:'Mauritania', MT:'Malta', MU:'Mauricio', MV:'Maldivas', MW:'Malaui',
  MX:'México', MY:'Malasia', MZ:'Mozambique', NA:'Namibia', NE:'Níger',
  NG:'Nigeria', NI:'Nicaragua', NL:'Países Bajos', NO:'Noruega', NP:'Nepal',
  NZ:'Nueva Zelanda', OM:'Omán', PA:'Panamá', PE:'Perú', PH:'Filipinas',
  PK:'Pakistán', PL:'Polonia', PT:'Portugal', PY:'Paraguay', QA:'Catar',
  RO:'Rumania', RS:'Serbia', RU:'Rusia', RW:'Ruanda', SA:'Arabia Saudita',
  SC:'Seychelles', SD:'Sudán', SE:'Suecia', SG:'Singapur', SI:'Eslovenia',
  SK:'Eslovaquia', SL:'Sierra Leona', SM:'San Marino', SN:'Senegal',
  SO:'Somalia', SR:'Surinam', SS:'Sudán del Sur', SV:'El Salvador',
  SY:'Siria', SZ:'Suazilandia', TD:'Chad', TG:'Togo', TH:'Tailandia',
  TJ:'Tayikistán', TL:'Timor Oriental', TM:'Turkmenistán', TN:'Túnez',
  TO:'Tonga', TR:'Turquía', TT:'Trinidad y Tobago', TZ:'Tanzania',
  UA:'Ucrania', UG:'Uganda', US:'Estados Unidos', UY:'Uruguay',
  UZ:'Uzbekistán', VC:'San Vicente y las Granadinas', VE:'Venezuela',
  VN:'Vietnam', VU:'Vanuatu', WS:'Samoa', YE:'Yemen',
  ZA:'Sudáfrica', ZM:'Zambia', ZW:'Zimbabue',
};

import QuantyEvolucionChart  from "@/components/charts/QuantyEvolucionChart.vue";
import AvMetodosPago         from "@/components/charts/AvMetodosPago.vue";
import PanelTipsterHeatmap   from "@/views/components/panel-tipster/PanelTipsterHeatmap.vue";
import metaInsightsService   from "@/services/metaInsights.service";

const CAMPANA_COLS_KEY = 'av_campana_cols_v1';
const CAMPANA_COLS_DEFAULT = {
  canal: true, pedidos: true, eur: true, usd: true, compradores: true, ticket: false,
};

export default {
  name: "AnalisisVentas",
  components: { QuantyEvolucionChart, AvMetodosPago, PanelTipsterHeatmap },

  directives: {
    clickOutside: {
      beforeMount(el, binding) {
        el._avClickOut = (e) => { if (!el.contains(e.target)) binding.value(e); };
        setTimeout(() => document.addEventListener('click', el._avClickOut), 0);
      },
      unmounted(el) { document.removeEventListener('click', el._avClickOut); },
    },
  },

  data() {
    return {
      cargando: false,
      exportandoQuantyJson: false,
      error: null,
      datos: null,
      chartKey: 0,
      // Modal detalle campaña
      detalleModal: { open: false, loading: false, campana_id: null, campana_nombre: '', meta_nombre: '', rows: [] },
      // Modal detalle producto
      productoModal: { open: false, loading: false, producto_id: null, producto_nombre: '', porCampana: [], sinCampana: null },
      // Tabla campañas
      campanaOrd:        { col: 'pedidos', dir: 'desc' },
      campanaPaginas:    {},
      // Tabla productos
      productoOrd:       { col: 'pedidos_total', dir: 'desc' },
      productoPaginas:   {},
      campanaColsVisible: { ...CAMPANA_COLS_DEFAULT },
      campanaColsOpen:   false,
      tipsters: [],
      canales: [],
      periodo: 'mes',
      periodos: [
        { value: 'mes',    label: 'Mes actual' },
        { value: 'hoy',    label: 'Hoy' },
        { value: 'ayer',   label: 'Ayer' },
        { value: 'anio',   label: 'Este año' },
        { value: 'all',    label: 'Todo' },
        { value: 'custom', label: 'Personalizar' },
      ],
      filters: {
        tipster_id: 0,
        canal_id:   0,
        moneda:     '',
        desde:      '',
        hasta:      '',
      },
    };
  },

  computed: {
    dateRange() {
      const fmt = d =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      if (this.periodo === 'all')    return { desde: null, hasta: null };
      if (this.periodo === 'custom') return { desde: this.filters.desde, hasta: this.filters.hasta };
      const hoy = new Date();
      if (this.periodo === 'hoy')  return { desde: fmt(hoy), hasta: fmt(hoy) };
      if (this.periodo === 'ayer') {
        const ayer = new Date(hoy);
        ayer.setDate(hoy.getDate() - 1);
        return { desde: fmt(ayer), hasta: fmt(ayer) };
      }
      if (this.periodo === 'mes') {
        const ini = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        return { desde: fmt(ini), hasta: fmt(hoy) };
      }
      if (this.periodo === 'anio') {
        const ini = new Date(hoy.getFullYear(), 0, 1);
        return { desde: fmt(ini), hasta: fmt(hoy) };
      }
      const meses = parseInt(this.periodo);
      const desde = new Date(hoy);
      desde.setMonth(desde.getMonth() - meses);
      return { desde: fmt(desde), hasta: fmt(hoy) };
    },

    canalesFiltrados() {
      if (!this.filters.tipster_id) return this.canales;
      return this.canales.filter(c => Number(c.tipster_id) === this.filters.tipster_id);
    },

    monedaEur() {
      const m = (this.datos?.por_moneda || []).find(m => m.moneda === 'EUR');
      return m ? { ...m, pedidos: Number(m.pedidos), ingresos: parseFloat(m.ingresos) } : { moneda: 'EUR', pedidos: 0, ingresos: 0 };
    },
    monedaUsd() {
      const m = (this.datos?.por_moneda || []).find(m => m.moneda === 'USD');
      return m ? { ...m, pedidos: Number(m.pedidos), ingresos: parseFloat(m.ingresos) } : { moneda: 'USD', pedidos: 0, ingresos: 0 };
    },
    ticketMedioEur() {
      const m = this.monedaEur;
      return m.pedidos > 0 ? m.ingresos / m.pedidos : 0;
    },
    ticketMedioUsd() {
      const m = this.monedaUsd;
      return m.pedidos > 0 ? m.ingresos / m.pedidos : 0;
    },
    monedaEurCampana() {
      const m = (this.datos?.por_moneda_campana || []).find(m => m.moneda === 'EUR');
      return m ? { ...m, pedidos: Number(m.pedidos), ingresos: parseFloat(m.ingresos) } : { moneda: 'EUR', pedidos: 0, ingresos: 0 };
    },
    monedaUsdCampana() {
      const m = (this.datos?.por_moneda_campana || []).find(m => m.moneda === 'USD');
      return m ? { ...m, pedidos: Number(m.pedidos), ingresos: parseFloat(m.ingresos) } : { moneda: 'USD', pedidos: 0, ingresos: 0 };
    },
    ticketMedioEurCampana() {
      const m = this.monedaEurCampana;
      return m.pedidos > 0 ? m.ingresos / m.pedidos : 0;
    },
    ticketMedioUsdCampana() {
      const m = this.monedaUsdCampana;
      return m.pedidos > 0 ? m.ingresos / m.pedidos : 0;
    },
    pctAtribuido() {
      const total = this.datos?.kpis?.total_pedidos || 0;
      const campana = this.datos?.kpis_campana?.total_pedidos || 0;
      return total > 0 ? Math.round((campana / total) * 100) : 0;
    },

    maxPaisPedidos() {
      if (!this.datos?.por_pais?.length) return 1;
      return Math.max(...this.datos.por_pais.map(r => Number(r.pedidos)));
    },

    kpisMoneda() {
      if (!this.datos) return [];
      return this.datos.por_moneda || [];
    },

    campanaPorTipster() {
      if (!this.datos?.por_campana?.length) return [];
      const grupos = {};
      for (const row of this.datos.por_campana) {
        const t = row.tipster_nombre || 'Sin tipster';
        if (!grupos[t]) grupos[t] = [];
        grupos[t].push(row);
      }
      return Object.entries(grupos).map(([tipster, rows]) => ({ tipster, rows }));
    },

    campanaColsConfig() {
      return [
        { key: 'canal',       label: 'Canal' },
        { key: 'pedidos',     label: 'Pedidos' },
        { key: 'eur',         label: 'EUR' },
        { key: 'usd',         label: 'USD' },
        { key: 'compradores', label: 'Compradores' },
        { key: 'ticket',      label: 'Ticket medio' },
      ];
    },

    productoPorTipster() {
      if (!this.datos?.por_producto?.length) return [];
      const grupos = {};
      for (const row of this.datos.por_producto) {
        const t = row.tipster_nombre || 'Sin tipster';
        if (!grupos[t]) grupos[t] = [];
        grupos[t].push(row);
      }
      return Object.entries(grupos).map(([tipster, rows]) => ({ tipster, rows }));
    },

    evolucionLabels() {
      if (!this.datos) return [];
      return this.datos.evolucion.map(r => r.fecha);
    },
    evolucionPedidos() {
      if (!this.datos) return [];
      return this.datos.evolucion.map(r => r.pedidos);
    },
    evolucionImporte() {
      if (!this.datos) return [];
      return this.datos.evolucion.map(r => parseFloat(r.ingresos));
    },
    evolucionRenovaciones() {
      if (!this.datos?.evolucion_renovaciones) return [];
      return this.datos.evolucion_renovaciones.map(r => r.pedidos);
    },
    totalRenovaciones() {
      if (!this.datos?.kpis_renovaciones) return 0;
      return (this.datos.kpis_renovaciones.total_eur || 0) + (this.datos.kpis_renovaciones.total_usd || 0);
    },
    totalRenovacionesCampana() {
      if (!this.datos?.kpis_renovaciones_campana) return 0;
      return (this.datos.kpis_renovaciones_campana.total_eur || 0) + (this.datos.kpis_renovaciones_campana.total_usd || 0);
    },
    renovPorCampanaSorted() {
      if (!this.datos?.renovaciones_por_campana) return [];
      return [...this.datos.renovaciones_por_campana].sort((a, b) =>
        (Number(b.importe_eur) + Number(b.importe_usd)) - (Number(a.importe_eur) + Number(a.importe_usd))
      );
    },
  },

  mounted() {
    this.cargarSelectores();
    this.loadCampanaCols();
  },

  methods: {
    flagClass(code) {
      const cc = String(code || '').trim().toLowerCase();
      return /^[a-z]{2}$/.test(cc) ? `fi-${cc}` : '';
    },
    paisNombre(code) {
      return NOMBRES_PAISES[String(code || '').trim().toUpperCase()] || code;
    },

    irInicio() {
      this.$router.push({ name: 'Dashboard' });
    },

    async cambiarPeriodo(p) {
      this.periodo = p;
      if (p !== 'custom') await this.cargar();
    },

    async cargarSelectores() {
      try {
        const rolId = Number(localStorage.getItem('usuarioRolId') || 0);
        const esPublicista = rolId === 5;
        const rTip = esPublicista
          ? await this.$store.dispatch('auth/misTipsters')
          : await this.$store.dispatch('auth/listaTipsters');
        this.tipsters = rTip?.data || rTip || [];

        const results = await Promise.all(
          this.tipsters.map(t =>
            this.$store.dispatch('auth/listaCanales', { tipster_id: Number(t.id) }).catch(() => [])
          )
        );
        const all = results.flatMap(r => r?.data || r || []);
        const map = new Map();
        for (const c of all) map.set(Number(c.id), c);
        this.canales = Array.from(map.values());
      } catch (e) {
        this.tipsters = [];
        this.canales  = [];
      }
    },

    async cargar() {
      this.cargando = true;
      this.error    = null;
      try {
        const { desde, hasta } = this.dateRange;
        const payload = {};
        if (this.filters.tipster_id) payload.tipster_id = this.filters.tipster_id;
        if (this.filters.canal_id)   payload.canal_id   = this.filters.canal_id;
        if (this.filters.moneda)     payload.moneda     = this.filters.moneda;
        if (desde) payload.desde = desde + ' 00:00:00';
        if (hasta) payload.hasta = hasta + ' 23:59:59';

        this.datos = await this.$store.dispatch('auth/analyticsVentas', payload);
        this.chartKey++;
      } catch (e) {
        this.error = 'Error al cargar los datos. Inténtalo de nuevo.';
      } finally {
        this.cargando = false;
      }
    },

    formatMoney(val, moneda) {
      const n = parseFloat(val) || 0;
      const sym = (moneda || 'EUR') === 'USD' ? '$' : '€';
      return sym + n.toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    // ── Modal detalle ───────────────────────────────────────────────────
    async abrirDetalle(row) {
      this.detalleModal = {
        open: true, loading: true,
        campana_id: row.campana_id,
        campana_nombre: row.campana_nombre,
        meta_nombre: row.meta_campaign_name || '',
        rows: [],
      };
      try {
        const res = await this.$store.dispatch('auth/analyticsVentasDetalle', { campana_id: row.campana_id });
        this.detalleModal.rows = res.productos || [];
      } catch (e) {
        this.detalleModal.rows = [];
      } finally {
        this.detalleModal.loading = false;
      }
    },
    cerrarDetalle() {
      this.detalleModal.open = false;
    },
    fmtFecha(dt) {
      if (!dt) return '—';
      return String(dt).slice(0, 10);
    },

    // ── Tabla campañas ──────────────────────────────────────────────────
    ordenarCampana(col) {
      if (this.campanaOrd.col === col) {
        this.campanaOrd.dir = this.campanaOrd.dir === 'asc' ? 'desc' : 'asc';
      } else {
        this.campanaOrd = { col, dir: 'desc' };
      }
      this.campanaPaginas = {};
    },
    campanaSortIcon(col) {
      if (this.campanaOrd.col !== col) return 'fas fa-sort opacity-4';
      return this.campanaOrd.dir === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },
    campanaRowsSorted(rows) {
      const { col, dir } = this.campanaOrd;
      return [...rows].sort((a, b) => {
        let va, vb;
        if (col === 'campana')     { va = a.campana_nombre; vb = b.campana_nombre; }
        else if (col === 'canal')  { va = a.canal_nombre;   vb = b.canal_nombre;   }
        else if (col === 'eur')    { va = parseFloat(a.importe_eur);       vb = parseFloat(b.importe_eur); }
        else if (col === 'usd')    { va = parseFloat(a.importe_usd);       vb = parseFloat(b.importe_usd); }
        else if (col === 'ticket') { va = parseFloat(a.ticket_medio);      vb = parseFloat(b.ticket_medio); }
        else if (col === 'compradores') { va = Number(a.compradores_unicos); vb = Number(b.compradores_unicos); }
        else { va = Number(a.pedidos); vb = Number(b.pedidos); }
        if (typeof va === 'string') return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        return dir === 'asc' ? va - vb : vb - va;
      });
    },
    campanaRowsPaged(rows, tipster) {
      const page = this.campanaGetPage(tipster);
      return rows.slice((page - 1) * 10, page * 10);
    },
    campanaTotalPages(rows)          { return Math.ceil(rows.length / 10); },
    campanaGetPage(tipster)          { return this.campanaPaginas[tipster] || 1; },
    campanaSetPage(tipster, page)    { this.campanaPaginas = { ...this.campanaPaginas, [tipster]: page }; },
    saveCampanaCols() {
      try { localStorage.setItem(CAMPANA_COLS_KEY, JSON.stringify(this.campanaColsVisible)); } catch (e) { /* storage no disponible */ }
    },
    loadCampanaCols() {
      try {
        const s = localStorage.getItem(CAMPANA_COLS_KEY);
        if (s) this.campanaColsVisible = { ...CAMPANA_COLS_DEFAULT, ...JSON.parse(s) };
      } catch (e) { /* storage no disponible */ }
    },
    resetCampanaCols() {
      this.campanaColsVisible = { ...CAMPANA_COLS_DEFAULT };
      this.saveCampanaCols();
    },

    formatMoneyEur(val) {
      return Number(val || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
    },

    formatMoneyUsd(val) {
      return Number(val || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
    },

    // ── Modal detalle producto ──────────────────────────────────────────
    async abrirDetalleProducto(row) {
      this.productoModal = {
        open: true, loading: true,
        producto_id: row.producto_id,
        producto_nombre: row.producto_nombre,
        porCampana: [], sinCampana: null,
      };
      try {
        const payload = { producto_id: row.producto_id };
        const { desde, hasta } = this.dateRange;
        if (desde) payload.desde = desde + ' 00:00:00';
        if (hasta) payload.hasta = hasta + ' 23:59:59';
        const res = await this.$store.dispatch('auth/analyticsVentasDetalleProducto', payload);
        this.productoModal.porCampana  = res.por_campana  || [];
        this.productoModal.sinCampana  = res.sin_campana  || null;
      } catch (e) {
        this.productoModal.porCampana = [];
      } finally {
        this.productoModal.loading = false;
      }
    },
    cerrarDetalleProducto() { this.productoModal.open = false; },

    // ── Tabla productos ─────────────────────────────────────────────────
    ordenarProducto(col) {
      if (this.productoOrd.col === col) {
        this.productoOrd.dir = this.productoOrd.dir === 'asc' ? 'desc' : 'asc';
      } else {
        this.productoOrd = { col, dir: 'desc' };
      }
      this.productoPaginas = {};
    },
    productoSortIcon(col) {
      if (this.productoOrd.col !== col) return 'fas fa-sort opacity-4';
      return this.productoOrd.dir === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },
    productoRowsSorted(rows) {
      const { col, dir } = this.productoOrd;
      return [...rows].sort((a, b) => {
        let va, vb;
        if      (col === 'producto')       { va = a.producto_nombre; vb = b.producto_nombre; }
        else if (col === 'canal')          { va = a.canal_nombre;    vb = b.canal_nombre;    }
        else if (col === 'eur')            { va = parseFloat(a.importe_eur);     vb = parseFloat(b.importe_eur); }
        else if (col === 'usd')            { va = parseFloat(a.importe_usd);     vb = parseFloat(b.importe_usd); }
        else if (col === 'campana')        { va = Number(a.pedidos_campana);     vb = Number(b.pedidos_campana); }
        else if (col === 'directo')        { va = Number(a.pedidos_directo);     vb = Number(b.pedidos_directo); }
        else if (col === 'compradores')    { va = Number(a.compradores_unicos);  vb = Number(b.compradores_unicos); }
        else                               { va = Number(a.pedidos_total);       vb = Number(b.pedidos_total); }
        if (typeof va === 'string') return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        return dir === 'asc' ? va - vb : vb - va;
      });
    },
    productoRowsPaged(rows, tipster) {
      const page = this.productoPaginas[tipster] || 1;
      return rows.slice((page - 1) * 10, page * 10);
    },
    productoTotalPages(rows)       { return Math.ceil(rows.length / 10); },
    productoGetPage(tipster)       { return this.productoPaginas[tipster] || 1; },
    productoSetPage(tipster, page) { this.productoPaginas = { ...this.productoPaginas, [tipster]: page }; },
    pctCampana(row) {
      const t = Number(row.pedidos_total);
      return t > 0 ? Math.round((Number(row.pedidos_campana) / t) * 100) : 0;
    },

    exportarJSON() {
      if (!this.datos) return;
      const blob = new Blob([JSON.stringify(this.datos, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `analisis_ventas_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
    async exportarQuantyJson() {
      if (this.exportandoQuantyJson) return;
      const { desde, hasta } = this.dateRange;
      if (!desde || !hasta) {
        this.error = 'Selecciona un período antes de descargar el Quanty JSON';
        return;
      }
      this.exportandoQuantyJson = true;
      try {
        const data = await metaInsightsService.exportarJson(desde, hasta, {
          tipster_id: this.filters.tipster_id || null,
          canal_id:   this.filters.canal_id   || null,
          ruta: 'analytics/export-json',
        });
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `quanty_${desde}_${hasta}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) {
        this.error = 'Error al generar el Quanty JSON';
      } finally {
        this.exportandoQuantyJson = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Filtros (estilo Panel Tipster) ──────────────────────────────── */
.av-selector-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
}
.av-select-block {
  display: flex;
  flex-direction: column;
  min-width: 140px;
}
.av-selector-actions {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  padding-bottom: 1px;
}

/* ── KPI Section headers ─────────────────────────────────────────── */
.av-kpi-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--q-border, #2a2f3d);
}
.av-kpi-section-label {
  font-size: .9rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: var(--q-muted, #8892a4);
}
.av-kpi-section-label--total {
  font-size: 1rem;
  color: var(--q-gold, #d4af37);
}
.av-kpi-section-label--campana { color: #26c6da; }
.av-kpi-section-label--renov { color: #a78bfa; }
.av-kpi-section-label--big { font-size: 1rem; }
.av-kpi-section-sub {
  font-size: .75rem;
  color: var(--q-muted, #8892a4);
  opacity: .7;
}

/* ── KPI Cards ───────────────────────────────────────────────────── */
.av-kpi-card {
  background: var(--q-card, #161b25);
  border: 1px solid var(--q-border, #2a2f3d);
  border-radius: 10px;
  padding: 16px 12px;
  text-align: center;
  transition: border-color .2s;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}
.av-kpi-card:hover { border-color: var(--q-gold, #d4af37); }

.av-kpi-icon {
  font-size: 20px;
  color: var(--q-gold, #d4af37);
  margin-bottom: 6px;
}
.av-kpi-valor {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--q-text, #e8eaf0);
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}
.av-kpi-label {
  font-size: .7rem;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: var(--q-text, #e8eaf0);
  opacity: .6;
  margin-top: 4px;
}

/* ── KPI sub-labels / sub-badges ─────────────────────────────── */
.av-kpi-sub {
  font-size: .72rem;
  color: var(--q-muted, #8892a4);
  margin-top: 3px;
}
.av-kpi-sub--usd { color: #26c6da; font-weight: 600; }

.av-kpi-sub-row {
  display: flex;
  gap: 4px;
  margin-top: 5px;
  justify-content: center;
  flex-wrap: wrap;
}
.av-kpi-sub-badge {
  display: inline-block;
  font-size: .68rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 20px;
  font-variant-numeric: tabular-nums;
}
.av-kpi-sub-badge.eur { background: rgba(212,175,55,.18); color: var(--q-gold, #d4af37); }
.av-kpi-sub-badge.usd { background: rgba(38,198,218,.14); color: #26c6da; }

/* ── KPI color variants ──────────────────────────────────────── */
.av-kpi-card--eur { border-color: rgba(212,175,55,.3); }
.av-kpi-card--eur:hover { border-color: var(--q-gold, #d4af37); }
.av-kpi-card--usd { border-color: rgba(38,198,218,.25); }
.av-kpi-card--usd:hover { border-color: #26c6da; }
.av-kpi-card--campana { border-color: rgba(38,198,218,.18); }
.av-kpi-card--campana:hover { border-color: #26c6da; }
.av-kpi-card--renov { border-color: rgba(167,139,250,.18); }
.av-kpi-card--renov:hover { border-color: #a78bfa; }
.av-kpi-card--renov-full { padding: 14px 18px; }
.av-kpi-renov-header { display: flex; align-items: center; margin-bottom: 10px; font-size: .85rem; }
.av-kpi-renov-body { display: flex; flex-direction: column; gap: 6px; }
.av-kpi-renov-row { display: flex; align-items: center; gap: 10px; font-size: 1.05rem; }

.av-kpi-icon--eur { color: var(--q-gold, #d4af37); }
.av-kpi-icon--usd { color: #26c6da; }
.av-kpi-icon--campana { color: #26c6da; }
.av-kpi-icon--renov { color: #a78bfa; }
.av-badge-campana { background: rgba(167,139,250,.15); color: #a78bfa; font-size: .7rem; }
.av-kpi-valor--eur { color: var(--q-gold, #d4af37); }
.av-kpi-valor--usd { color: #26c6da; }

/* ── Tabla ──────────────────────────────────────────────────────── */
.av-table th {
  font-size: .7rem;
  text-transform: uppercase;
  letter-spacing: .05em;
  opacity: .6;
  padding: 10px 14px;
  white-space: nowrap;
}
.av-table td {
  padding: 9px 14px;
  font-size: .82rem;
  border-bottom: 1px solid var(--q-border, #2a2f3d);
}
.av-num {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.badge-moneda {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: .7rem;
  font-weight: 700;
}
.moneda-eur { background: rgba(212,175,55,.15); color: #d4af37; }
.moneda-usd { background: rgba(79,195,247,.15); color: #4fc3f7; }

/* ── Tabla campañas ────────────────────────────────────────────── */
.av-tabla-titulo { color: var(--q-gold, #d4af37); font-size: 1rem; font-weight: 700; }

.av-td-campana { font-weight: 600; color: var(--q-text, #e8eaf0); }
.av-meta-name  { color: var(--q-muted, #8892a4); font-size: .7rem; font-weight: 400; margin-top: 2px; }

.tc-th--eur { color: var(--q-gold, #d4af37) !important; }
.tc-th--usd { color: #26c6da !important; }
.av-num--eur { color: var(--q-gold, #d4af37); }
.av-num--usd { color: #26c6da; }

.av-count-badge {
  display: inline-block;
  font-size: .68rem;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 20px;
  margin-right: 3px;
  font-variant-numeric: tabular-nums;
}
.av-count-badge.eur { background: rgba(212,175,55,.18); color: var(--q-gold, #d4af37); }
.av-count-badge.usd { background: rgba(38,198,218,.14); color: #26c6da; }
.opacity-4 { opacity: .4; }

/* Cabeceras ordenables */
.av-th-sort {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.av-th-sort:hover { color: var(--q-gold, #d4af37); }
.av-th-sort i { font-size: .65rem; margin-left: 3px; opacity: .6; }
.av-th-sort:hover i { opacity: 1; }

/* Separador de tipster */
.av-tipster-sep {
  font-size: .8rem;
  font-weight: 700;
  color: var(--q-muted, #8892a4);
  padding: 6px 0 4px;
  text-transform: uppercase;
  letter-spacing: .06em;
}

/* Paginación */
.av-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 10px 16px;
  border-top: 1px solid var(--q-border, #2a2f3d);
}
.av-page-btn {
  background: var(--q-card, #161b25);
  border: 1px solid var(--q-border, #2a2f3d);
  color: var(--q-text, #e8eaf0);
  border-radius: 6px;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: .75rem;
  transition: border-color .15s;
}
.av-page-btn:hover:not(:disabled) { border-color: var(--q-gold, #d4af37); color: var(--q-gold, #d4af37); }
.av-page-btn:disabled { opacity: .35; cursor: not-allowed; }
.av-page-info { font-size: .8rem; color: var(--q-muted, #8892a4); }

/* Selector de columnas */
.av-col-wrap { position: relative; }
.av-col-btn {
  background: var(--q-card, #161b25);
  border: 1px solid var(--q-border, #2a2f3d);
  color: var(--q-muted, #8892a4);
  border-radius: 6px;
  padding: 5px 10px;
  cursor: pointer;
  font-size: .78rem;
  transition: border-color .15s, color .15s;
}
.av-col-btn:hover, .av-col-btn.activo { border-color: var(--q-gold, #d4af37); color: var(--q-gold, #d4af37); }
.av-col-panel {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  background: var(--q-card, #161b25);
  border: 1px solid var(--q-border, #2a2f3d);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,.45);
  padding: 12px;
  z-index: 200;
  min-width: 180px;
}
.av-col-panel-hdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: .75rem;
  font-weight: 700;
  color: var(--q-text, #e8eaf0);
  text-transform: uppercase;
  letter-spacing: .06em;
}
.av-col-reset {
  background: none;
  border: none;
  color: var(--q-gold, #d4af37);
  font-size: .72rem;
  cursor: pointer;
  padding: 0;
}
.av-col-panel-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.av-col-item {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: .8rem;
  color: var(--q-text, #e8eaf0);
  cursor: pointer;
}
.av-col-item input { accent-color: var(--q-gold, #d4af37); cursor: pointer; }

/* ── Botón ojo ─────────────────────────────────────────────────── */
.av-ojo-btn {
  background: none;
  border: 1px solid var(--q-border, #2a2f3d);
  border-radius: 5px;
  color: var(--q-muted, #8892a4);
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: .72rem;
  transition: border-color .15s, color .15s;
}
.av-ojo-btn:hover { border-color: var(--q-gold, #d4af37); color: var(--q-gold, #d4af37); }

/* ── Modal ─────────────────────────────────────────────────────── */
.av-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.65);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.av-modal-box {
  background: var(--q-card, #161b25);
  border: 1px solid var(--q-border, #2a2f3d);
  border-radius: 14px;
  box-shadow: 0 16px 48px rgba(0,0,0,.6);
  width: 100%;
  max-width: 780px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.av-modal-hdr {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--q-border, #2a2f3d);
  flex-shrink: 0;
}
.av-modal-titulo { color: var(--q-gold, #d4af37); font-size: .95rem; }
.av-modal-close {
  background: none;
  border: none;
  color: var(--q-muted, #8892a4);
  font-size: 1rem;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}
.av-modal-close:hover { color: var(--q-text, #e8eaf0); }
.av-modal-body { overflow-y: auto; padding: 0; }

/* Transición del modal */
.av-modal-fade-enter-active, .av-modal-fade-leave-active { transition: opacity .18s; }
.av-modal-fade-enter-from, .av-modal-fade-leave-to { opacity: 0; }

@media (max-width: 576px) {
  .av-modal-box { max-height: 92vh; border-radius: 10px; }
}

/* ── Error / Empty ─────────────────────────────────────────────── */
.av-error {
  background: rgba(220,53,69,.15);
  border: 1px solid rgba(220,53,69,.4);
  border-radius: 8px;
  color: #ff6b6b;
  padding: 12px 16px;
  font-size: .88rem;
}
.av-empty {
  text-align: center;
  padding: 48px 16px;
  opacity: .5;
}
.av-empty-icon {
  font-size: 3rem;
  color: var(--q-gold, #d4af37);
  margin-bottom: 12px;
  display: block;
}

/* ── Filtros generales ─────────────────────────────────────────── */
.filter-grid { display: flex; flex-direction: column; gap: 12px; }
.filter-inputs { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.filter-field { display: flex; flex-direction: column; min-width: 140px; }
.filter-bottom { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.filter-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.select-wrap { position: relative; }

.btn-accion {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 16px; border-radius: 6px; font-size: .8rem;
  font-weight: 600; cursor: pointer; border: none; transition: opacity .15s;
}
.btn-accion:disabled { opacity: .5; cursor: not-allowed; }
.btn-accion--primary  { background: var(--q-gold, #d4af37); color: #0d1117; }
.btn-accion--secondary{ background: var(--q-border, #2a2f3d); color: var(--q-text, #e8eaf0); }

/* ── Filtro de período ─────────────────────────────────────────── */
.filtro-periodo {
  border-top: 1px solid var(--q-border, #2a2f3d);
  padding-top: 14px;
}
.periodo-btns { display: flex; gap: 6px; flex-wrap: wrap; }
.btn-periodo {
  padding: 5px 14px;
  border-radius: 8px;
  border: 1px solid var(--q-border-md, #3a3f52);
  background: transparent;
  color: var(--q-muted, #8892a4);
  font-size: .8rem;
  cursor: pointer;
  transition: color .15s, border-color .15s, background .15s;
  white-space: nowrap;
}
.btn-periodo:hover:not(:disabled) { border-color: var(--q-gold, #d4af37); color: var(--q-text, #e8eaf0); }
.btn-periodo.active {
  background: var(--q-gold, #d4af37);
  border-color: var(--q-gold, #d4af37);
  color: #140c00;
  font-weight: 600;
}
.btn-periodo:disabled { opacity: .5; cursor: not-allowed; }

.custom-dates {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.date-input {
  max-width: 150px;
  font-size: .85rem;
  background: var(--q-card, #161b25);
  color: var(--q-text, #e8eaf0);
  border-color: var(--q-border-md, #3a3f52);
}
.date-input:focus { border-color: var(--q-gold, #d4af37); box-shadow: none; }
.sep-dash { color: var(--q-muted, #8892a4); font-size: .9rem; }

/* ── Placeholder inicial ─────────────────────────────────────────── */
.av-placeholder {
  opacity: .5;
}
.av-placeholder-icon {
  font-size: 3rem;
  color: var(--q-gold, #d4af37);
  opacity: .4;
}
.av-placeholder-text {
  color: var(--q-text, #e8eaf0);
  font-size: .95rem;
}

/* ── Lista países con banderas ───────────────────────────────────── */
.av-paises-lista { display: flex; flex-direction: column; gap: 5px; }
.av-pais-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}
.av-pais-flag { font-size: 18px; flex-shrink: 0; width: 22px; }
.av-pais-nombre {
  font-size: .8rem;
  color: var(--q-text, #e8eaf0);
  width: 130px;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.av-pais-bar-wrap {
  flex: 1;
  background: rgba(255,255,255,.05);
  border-radius: 4px;
  height: 8px;
  overflow: hidden;
}
.av-pais-bar {
  height: 100%;
  background: rgba(245,166,35,.75);
  border-radius: 4px;
  transition: width .3s ease;
  min-width: 2px;
}
.av-pais-num {
  font-size: .8rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--q-muted, #8892a4);
  width: 36px;
  text-align: right;
  flex-shrink: 0;
}

/* Ventas totales — nuevos elementos */
.av-tabla-sub {
  font-size: .75rem;
  color: var(--q-muted, #8a8fa8);
  font-style: italic;
}
.av-campana-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(212,175,55,.14);
  color: var(--q-gold, #d4af37);
  border-radius: 4px;
  padding: 1px 6px;
  font-size: .78rem;
  font-weight: 600;
  margin-right: 4px;
}
.av-pct-campana {
  font-size: .75rem;
  color: var(--q-muted, #8a8fa8);
}
.av-modal-box--lg { max-width: 680px; width: 96vw; }
.av-modal-seccion-titulo {
  font-size: .78rem;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: var(--q-muted, #8a8fa8);
  margin: 0 0 8px;
  padding: 0 16px;
}
.av-modal-seccion-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(212,175,55,.18);
  color: var(--q-gold, #d4af37);
  border-radius: 50%;
  width: 18px; height: 18px;
  font-size: .7rem;
  font-weight: 700;
  margin-left: 6px;
  vertical-align: middle;
}
.av-directo-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: rgba(255,255,255,.03);
  border-radius: 6px;
  font-size: .85rem;
}

/* Responsive */
@media (max-width: 576px) {
  .av-kpi-card { padding: 12px 8px; }
  .av-kpi-valor { font-size: 1.05rem; }
  .filter-field { min-width: 100%; }
  .btn-periodo { font-size: .75rem; padding: 4px 10px; }
  .date-input { max-width: calc(50vw - 40px); }
  .av-moneda-card { min-width: 140px; }
}
</style>
